<template>
    <section class="flex flex-col gap-3" :style="{ '--self-score-accent': fillColor }">
        <h2 class="body-small-semibold text-ink">Category Scores</h2>
        <p v-if="intro" class="body-small text-ink-muted">{{ intro }}</p>
        <BaseCard padding="lg" class="flex flex-col gap-5">
            <div v-for="section in visibleSections" :key="section.section_id" class="flex flex-col gap-2.5">
                <div class="flex items-center justify-between body-small text-ink">
                    <span>{{ section.title || "Questions" }}</span>
                    <!-- Interpreted sections mix reverse-keyed statements, so a
                         raw average would mislead: the title stays, the number goes. -->
                    <span v-if="!section.has_interpretation" class="body-small">
                        <span class="body-small-bold">{{ formatAvg(section.avg) }}</span
                        ><span class="text-ink-muted">/{{ maxScore }}</span>
                    </span>
                </div>
                <!-- Score bar: cream track, accent fill. Announced to
                     assistive tech via the ARIA attributes below. -->
                <div
                    v-if="!section.has_interpretation"
                    class="self-score-track"
                    role="progressbar"
                    :aria-valuenow="section.avg ?? 0"
                    :aria-valuemin="0"
                    :aria-valuemax="maxScore"
                    :aria-label="`${section.title || 'Questions'} average score`">
                    <span class="self-score-fill" :style="{ width: `${percent(section.avg)}%` }"></span>
                </div>
            </div>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import type { SectionAggregate } from "~vue/api/generated/reflections-admin";
import BaseCard from "~vue/components/BaseCard.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";

interface Props {
    sections: SectionAggregate[];
    maxScore: number;
    // Optional descriptive line below the heading. Used on the admin campaign
    // report to explain what the rolled-up bars represent.
    intro?: string;
    // Bar fill. A reflection passes its own family color so the report reads
    // as part of the flow that produced it; the admin campaign report has its
    // own palette and keeps the brand magenta.
    accentColor?: string;
}

const props = defineProps<Props>();

const fillColor = computed(() => props.accentColor ?? "var(--color-accent)");

const visibleSections = computed(() => props.sections.filter((s) => s.avg !== null));

function percent(avg: number | null): number {
    if (avg === null || !props.maxScore) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round((avg / props.maxScore) * 100)));
}

function formatAvg(avg: number | null): string {
    return formatNumber(avg, "one-decimal") || "—";
}
</script>

<style scoped>
.self-score-track {
    height: 17px;
    background: var(--color-surface-sunken);
    border-radius: 5px;
    padding: 0;
    overflow: hidden;
    display: block;
}

.self-score-fill {
    display: block;
    height: 100%;
    background: var(--self-score-accent);
    border-radius: 5px;
    transition: width 320ms ease;
}

@media (prefers-reduced-motion: reduce) {
    .self-score-fill {
        transition: none;
    }
}
</style>
